import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, AlertTriangle, Zap } from 'lucide-react';

// PREVIEW MODE - Set to true for UI demo without backend
const PREVIEW_MODE = false;

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // PREVIEW MODE - Bypass authentication for UI demo
    if (PREVIEW_MODE) {
      await new Promise(resolve => setTimeout(resolve, 800)); // Simulate loading
      toast.success('ACCESS GRANTED - WELCOME COMMANDER', {
        style: { background: '#000', border: '1px solid #00FF9D', color: '#00FF9D' }
      });
      // Set demo user in localStorage for preview
      localStorage.setItem('selena_token', 'PREVIEW_MODE_TOKEN');
      localStorage.setItem('selena_user', JSON.stringify({ username: username || 'COMMANDER', role: 'admin' }));
      setLoading(false);
      // Force page reload to trigger auth context update
      window.location.href = '/';
      return;
    }

    try {
      await login(username, password);
      toast.success('ACCESS GRANTED - WELCOME COMMANDER');
      navigate('/');
    } catch (err) {
      setError('ACCESS DENIED - INVALID CREDENTIALS');
      toast.error('Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(220, 20, 60, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(220, 20, 60, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#DC143C] opacity-5 blur-[150px] rounded-full" />

      <div className="w-full max-w-md relative animate-fade-in">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-3 h-3 bg-[#DC143C] animate-pulse-glow" />
            <h1 className="font-heading text-4xl font-bold tracking-[0.2em] text-[#EDEDED] uppercase">
              SELENA MATRIX
            </h1>
            <div className="w-3 h-3 bg-[#DC143C] animate-pulse-glow" />
          </div>
          <p className="font-mono text-xs text-[#888888] tracking-widest uppercase">
            THE GLOBAL COMMANDER // DNCM SOLUTIONS
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-[#0A0A0A] border border-white/10 relative corner-accent">
          {/* Card Header */}
          <div className="module-header">
            <div className="flex items-center gap-3">
              <Lock className="w-4 h-4 text-[#DC143C]" />
              <span className="module-title">ACCESS CONTROL</span>
            </div>
            <span className="module-badge">SECURE</span>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {error && (
              <div className="flex items-center gap-3 p-4 bg-[#DC143C]/10 border border-[#DC143C]/50">
                <AlertTriangle className="w-5 h-5 text-[#DC143C]" />
                <span className="font-mono text-sm text-[#DC143C]">{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <label className="font-heading text-xs uppercase tracking-widest text-[#888888]">
                OPERATOR ID
              </label>
              <input
                data-testid="login-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="tactical-input"
                placeholder="Operator ID"
              />
            </div>

            <div className="space-y-2">
              <label className="font-heading text-xs uppercase tracking-widest text-[#888888]">
                ACCESS CODE
              </label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="tactical-input pr-12"
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#888888] hover:text-[#EDEDED] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-4 text-base disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-3">
                  <span className="w-2 h-2 bg-white animate-pulse" />
                  AUTHENTICATING...
                </span>
              ) : (
                'INITIATE ACCESS'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="border-t border-white/10 p-4 flex justify-between items-center">
            <span className="font-mono text-[10px] text-[#444444]">
              SYS.VER.1.0.0 | <a href="/forgot-password" style={{color:"#DC143C",fontFamily:"monospace",fontSize:"10px",textDecoration:"none"}}>MOT DE PASSE OUBLIÉ ?</a>
            </span>
            <span className="font-mono text-[10px] text-[#444444]">
              ENCRYPTED CONNECTION
            </span>
          </div>
        </div>

        {/* Bottom text */}
        <p className="text-center mt-8 font-mono text-[10px] text-[#444444] tracking-wider">
          UNAUTHORIZED ACCESS WILL BE LOGGED AND TRACED
        </p>

        {/* Preview Mode Indicator */}
        {PREVIEW_MODE && (
          <div className="mt-6 flex items-center justify-center gap-2 opacity-60">
            <Zap className="w-3 h-3 text-[#00FF9D]" />
            <span className="font-mono text-[10px] text-[#00FF9D] tracking-wider">
              PREVIEW MODE ACTIVE - CLICK TO ENTER
            </span>
          </div>
        )}
      </div>
    </div>
  );
}


